/* eslint-disable no-plusplus */
import React, { FC, useContext, useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import { NumberParam, StringParam, useQueryParam } from 'use-query-params';
import { useDebouncedCallback } from 'use-debounce';
import { DatePickerContext } from '@/features/details/date-pick/context';
import {
  ExpVersion,
  SummaryResponse,
  useAnalyticsApi,
} from '@/webapi/use-analytics-api';
import {
  maybe,
  notProfitMetric,
  notReturnsMetric,
} from '@/features/details/utils';
import {
  getAllowedGoals,
  isProfitMetricAvailable,
  isReturnsMetricAvailable,
  MetricKind,
} from '@/webapi/models';
import {
  Experience,
  ExperienceTag,
  ExperienceVariant,
} from '@/webapi/use-experience-api';
import { CONTROL, DELETED, isAudience } from '@/features/details/shared';
import { shouldDisplaySummaryMetrics } from '@/utils/data_freshness';

interface SummaryContext {
  showMoreMetrics: boolean;
  showSummary: boolean;
  hasOneVariant: boolean;
  primaryGoal: MetricKind;
  data: SummaryResponse;
  variants: Array<ExperienceVariant>;
  moreMetrics: Array<MetricKind>;
  primaryMetrics: Array<MetricKind>;
  largeHeaders: boolean;
  hasMoreMetrics: boolean;
  metricsPerRow: number;
  metricsStartAt?: Date;
}

export const SummaryContext = React.createContext({} as SummaryContext);

export const SummaryProvider: FC<{ experience: Experience }> = ({
  experience,
  children,
}) => {
  const summaryContext = newSummaryContext(experience);

  return (
    <SummaryContext.Provider value={summaryContext}>
      {children}
    </SummaryContext.Provider>
  );
};

function isVariantOn100(experience: Experience, version: ExpVersion) {
  const variantWith100Chance = experience?.variants?.find(
    (v) => v.publishedChance === 100,
  );
  return (
    !!variantWith100Chance &&
    variantWith100Chance?.name !== CONTROL &&
    version?.isCurrent
  );
}

function getVariants(
  data: SummaryResponse,
  experience: Experience,
  variantOn100: boolean,
) {
  const ids = data?.variants?.map((x) => x.variantId);
  let variants =
    experience?.variants?.filter((v) => !!ids?.includes(v.id)) || [];
  variants = [
    ...variants,
    ...maybe(
      () =>
        data.variants
          .filter((v) => !variants.map((v) => v.id).includes(v.variantId))
          .map(
            (v) => ({ id: v.variantId, name: DELETED } as ExperienceVariant),
          ),
      [],
    ),
  ];
  return variantOn100 ? variants?.filter((v) => v.name !== CONTROL) : variants;
}

export function newSummaryContext(experience: Experience) {
  const { version, startDate, endDate } = useContext(DatePickerContext);
  const { getSummary, loading } = useAnalyticsApi();
  const [data, setData] = useState<SummaryResponse>(undefined);
  const [v] = useQueryParam(`v`, NumberParam);
  const [sessionsType] = useQueryParam(`st`, StringParam);
  const [audience] = useQueryParam(`aud`, StringParam);

  const activeVariants = Object.keys(
    experience?.versions?.find((v) => v?.version === version?.version)
      ?.variantChances || {},
  );

  const debouncedDataFEtch = useDebouncedCallback(summaryDataFetch, 300);
  useAsyncEffect(async () => {
    await debouncedDataFEtch(
      startDate,
      endDate,
      getSummary,
      experience,
      v,
      version,
      setData,
      sessionsType,
      audience,
      activeVariants,
    );
  }, [
    startDate?.toLocaleDateString(),
    endDate?.toLocaleDateString(),
    v,
    sessionsType,
    audience,
  ]);
  const variantOn100 = isVariantOn100(experience, version);
  const variants = getVariants(data, experience, variantOn100);

  const goals = getAllowedGoals(experience?.goals, experience.tag);
  const primaryGoal = experience?.primaryGoal || goals?.[0];

  const hasOneVariant = variants?.length === 1 || variantOn100;
  const showSummary = shouldDisplaySummaryMetrics(data, experience, version);

  const showMoreMetrics = goals?.length > 0 && showSummary;

  const isContent = experience.tag === ExperienceTag.CONTENT;
  const primaryMetrics = new Set([
    MetricKind.SESSIONS,
    MetricKind.TRANSACTIONS,
    MetricKind.CONVERSION_RATE,
    MetricKind.AVG_ORDER_VALUE,
    MetricKind.PER_SESSION_VALUE,
    MetricKind.REVENUE,
  ]);

  const contentMetrics = [
    MetricKind.CLICKS,
    MetricKind.CTR,
    MetricKind.REVENUE_FROM_CLICKS,
  ];

  const freeSlotsInFirstRow = calcFreeSlotsInFirstRow({
    hasOneVariant,
  });
  let moreMetrics = goals?.filter((m) => !primaryMetrics.has(m));
  if (isContent) {
    const set = new Set(moreMetrics);
    contentMetrics.forEach((m) => set.add(m));
    moreMetrics = Array.from(set);
  }
  if (!isProfitMetricAvailable(experience.metricsStartAt)) {
    moreMetrics = moreMetrics?.filter(notProfitMetric());
  }
  if (!isReturnsMetricAvailable(experience.metricsStartAt)) {
    moreMetrics = moreMetrics?.filter(notReturnsMetric());
  }
  for (let i = 0; i < freeSlotsInFirstRow && !!moreMetrics?.[i]; i++) {
    primaryMetrics.add(moreMetrics?.[i]);
  }
  moreMetrics = moreMetrics?.filter((m) => !primaryMetrics.has(m));

  const hasMoreMetrics = showMoreMetrics && moreMetrics.length > 0;
  const metricsPerRow = primaryMetrics.size + (!hasOneVariant ? 2 : 0);
  const largeHeaders = metricsPerRow <= 6;

  const metricsStartAt: Date | undefined =
    experience.metricsStartAt && new Date(experience.metricsStartAt);
  return {
    metricsStartAt,
    showMoreMetrics,
    showSummary,
    primaryGoal,
    data,
    hasOneVariant,
    variants,
    largeHeaders,
    hasMoreMetrics,
    moreMetrics,
    metricsPerRow,
    primaryMetrics: Array.from(primaryMetrics),
    loading,
  };
}

async function summaryDataFetch(
  startDate: Date,
  endDate: Date,
  getSummary: (
    experienceId: string,
    goal: MetricKind,
    startAt: Date,
    endAt: Date,
    version?: number,
    expEndDate?: string | undefined,
    sessionType?: string,
    audience?: string,
    activeVariants?: string[],
  ) => Promise<SummaryResponse>,
  experience: Experience,
  v: number,
  version: ExpVersion,
  setData: (
    value: ((prevState: SummaryResponse) => SummaryResponse) | SummaryResponse,
  ) => void,
  sessionType?: string,
  audience?: string,
  activeVariants?: string[],
) {
  if (startDate && endDate) {
    const versionId = isAudience() ? -1 : v || version?.version;
    const metrics = await getSummary(
      experience.id,
      experience.primaryGoal || experience.goals?.[0],
      startDate,
      endDate,
      versionId,
      experience?.archivedAt || experience?.endDate,
      sessionType,
      audience,
      activeVariants,
    );
    setData(metrics);
  }
}

function calcFreeSlotsInFirstRow({
  hasOneVariant,
}: {
  hasOneVariant: boolean;
}) {
  let freeSlotsInFirstRow = 0; // 6 recs metrics + variant name + odds to win, 0 free slots
  if (hasOneVariant) {
    freeSlotsInFirstRow = 2; // 6 recs/custom metrics ,2 free slots
  }
  return freeSlotsInFirstRow;
}
